import React, { useEffect, useState } from "react";
import { Plus, X } from "lucide-react";

const API = process.env.REACT_APP_API_BASE || "http://127.0.0.1:8000";
const card = "rounded-xl border border-[#00ff90]/30 bg-[#0d0d0d] shadow-[0_0_12px_#00ff9022]";
const inputCls = "bg-black border border-[#00ff90]/20 rounded-lg px-3 py-2 text-sm text-white outline-none focus:border-[#00ff90]/50 transition-colors";
const selectCls = "bg-black border border-[#00ff90]/20 rounded-lg px-2 py-1.5 text-sm text-white outline-none focus:border-[#00ff90]/40 transition-colors";

export default function WorkflowSettings() {
  const [states,  setStates]  = useState([]);
  const [matrix,  setMatrix]  = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving,  setSaving]  = useState(false);
  const [newState,setNewState]= useState("");
  const [confirm, setConfirm] = useState(null);
  const [msg,     setMsg]     = useState(null);
  const token = localStorage.getItem("token");

  useEffect(() => {
    fetch(`${API}/api/admin/tickets/workflow`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json())
      .then(d => { setStates(Array.isArray(d.states) ? d.states : []); setMatrix(Array.isArray(d.priority_matrix) ? d.priority_matrix : []); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const save = async () => {
    setSaving(true); setMsg(null);
    try {
      const res = await fetch(`${API}/api/admin/tickets/workflow`, {
        method: "PUT", headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ states, priority_matrix: matrix }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setMsg("✓ Workflow saved.");
    } catch (e) { setMsg(`⚠ ${e.message}`); }
    finally { setSaving(false); }
  };

  const addState = () => { const s = newState.trim(); if (!s || states.includes(s)) return; setStates(p => [...p, s]); setNewState(""); };
  const removeState = (s) => { setStates(p => p.filter(x => x !== s)); setConfirm(null); };
  const updateMatrix = (i, k, v) => setMatrix(p => p.map((r, idx) => idx === i ? { ...r, [k]: v } : r));

  if (loading) return <div className={`${card} p-5`}><div className="h-24 rounded-lg bg-white/5 animate-pulse" /></div>;

  return (
    <>
      <div className={card}>
        <div className="px-5 py-4 border-b border-white/5 flex items-center justify-between">
          <span className="text-xs text-[#00ff90]/60 uppercase tracking-widest font-medium">Workflow & Priority Matrix</span>
          <button onClick={save} disabled={saving}
            className="text-xs px-4 py-1.5 rounded-lg bg-[#00ff90]/15 text-[#00ff90] border border-[#00ff90]/30 hover:bg-[#00ff90]/25 disabled:opacity-40 transition-colors">
            {saving ? "Saving…" : "Save"}
          </button>
        </div>

        <div className="p-5 space-y-5">
          {/* States */}
          <div>
            <p className="text-xs text-[#00ff90]/50 uppercase tracking-wider mb-3">Ticket States</p>
            <div className="flex gap-2 mb-3">
              <input value={newState} onChange={e => setNewState(e.target.value)} placeholder="New state…"
                onKeyDown={e => e.key === "Enter" && addState()}
                className={`${inputCls} flex-1`} />
              <button onClick={addState}
                className="flex items-center gap-1 text-xs px-3 py-2 rounded-lg border border-[#00ff90]/25 bg-[#00ff90]/8 text-[#00ff90]/70 hover:bg-[#00ff90]/15 hover:text-[#00ff90] transition-colors">
                <Plus size={12} /> Add
              </button>
            </div>
            <div className="flex flex-wrap gap-2">
              {states.map(s => (
                <span key={s} className="flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full border border-[#00ff90]/20 bg-[#00ff90]/5 text-[#00ff90]/70">
                  {s}
                  <button onClick={() => setConfirm(s)} className="text-[#00ff90]/30 hover:text-red-400 transition-colors"><X size={10} /></button>
                </span>
              ))}
            </div>
          </div>

          {/* Priority matrix */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <p className="text-xs text-[#00ff90]/50 uppercase tracking-wider">Impact × Urgency → Priority</p>
              <button onClick={() => setMatrix(p => [...p, { impact: "Medium", urgency: "Medium", priority: "P3" }])}
                className="text-xs px-3 py-1 rounded-lg border border-white/10 text-white/40 hover:text-white transition-colors">
                + Row
              </button>
            </div>
            <div className="space-y-2">
              {matrix.length === 0 ? <p className="text-white/20 text-xs italic">No matrix rows.</p>
              : matrix.map((r, i) => (
                <div key={i} className="grid grid-cols-4 gap-2">
                  <select value={r.impact}   onChange={e => updateMatrix(i, "impact",   e.target.value)} className={`${selectCls} w-full`}>
                    {["High","Medium","Low"].map(o => <option key={o}>{o}</option>)}
                  </select>
                  <select value={r.urgency}  onChange={e => updateMatrix(i, "urgency",  e.target.value)} className={`${selectCls} w-full`}>
                    {["High","Medium","Low"].map(o => <option key={o}>{o}</option>)}
                  </select>
                  <select value={r.priority} onChange={e => updateMatrix(i, "priority", e.target.value)} className={`${selectCls} w-full`}>
                    {["P1","P2","P3","P4"].map(o => <option key={o}>{o}</option>)}
                  </select>
                  <button onClick={() => setMatrix(p => p.filter((_, idx) => idx !== i))}
                    className="text-xs px-2 py-1.5 rounded-lg border border-red-500/20 text-red-400/60 hover:bg-red-500/10 hover:text-red-400 transition-colors">
                    Delete
                  </button>
                </div>
              ))}
            </div>
          </div>

          {msg && <p className={`text-xs ${msg.startsWith("⚠") ? "text-red-400" : "text-[#00ff90]"}`}>{msg}</p>}
        </div>
      </div>

      {/* Confirm remove state modal */}
      {confirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/70" onClick={() => setConfirm(null)} />
          <div className="relative rounded-xl border border-[#00ff90]/20 bg-[#0d0d0d] p-5 w-80">
            <p className="font-semibold text-white mb-2">Remove state "{confirm}"?</p>
            <p className="text-sm text-white/50 mb-4">This may affect tickets currently in this state.</p>
            <div className="flex justify-end gap-2">
              <button onClick={() => setConfirm(null)} className="text-xs px-4 py-2 rounded-lg border border-white/10 text-white/40 hover:text-white transition-colors">Cancel</button>
              <button onClick={() => removeState(confirm)} className="text-xs px-4 py-2 rounded-lg bg-red-500/15 text-red-400 border border-red-500/30 hover:bg-red-500/25 transition-colors">Remove</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}